/**
 * Structured logging for the web app, sent to PostHog Logs.
 *
 * Prefer these helpers over a bare `console.*`. Console output is captured too
 * (`logs.captureConsoleLogs` in `./posthog.ts`), but it arrives as a flattened string with no
 * structured attributes and no trace correlation, and it depends on `./logRedaction.ts` to
 * catch anything sensitive that was printed by accident.
 *
 * Records are correlated to the active OpenTelemetry span (see `./tracing.ts`) so a web log
 * lands inside the same trace waterfall as the API logs it triggered.
 */
import { trace } from "@opentelemetry/api";
import posthog from "posthog-js";

type Attributes = Record<string, unknown>;

// When PostHog is running it captures console output as well, so echoing there too would file
// every log twice. The echo is for the case where there is no project to read: no token.
const posthogCapturesConsole = Boolean(
  import.meta.env.VITE_POSTHOG_PROJECT_TOKEN as string | undefined
);

/** Trace/span ids of the active span, so the log renders inside that trace. */
function traceContext(): { trace_id?: string; span_id?: string } {
  try {
    const spanContext = trace.getActiveSpan()?.spanContext();
    if (!spanContext) return {};
    return { trace_id: spanContext.traceId, span_id: spanContext.spanId };
  } catch {
    return {};
  }
}

function emit(
  level: "info" | "warn" | "error",
  message: string,
  attributes?: Attributes
): void {
  if (!posthogCapturesConsole) {
    console[level](message, attributes ?? "");
  }
  try {
    posthog.captureLog({
      body: message,
      level,
      attributes: { ...attributes, app: "web" },
      ...traceContext(),
    });
  } catch {
    // Never let logging break the calling path.
  }
}

/**
 * Normalize a thrown value into loggable attributes.
 *
 * Takes the message and name only — never the whole object. Errors from fetch and Apollo can
 * carry request bodies and headers, which is how a bearer token ends up somewhere it shouldn't.
 */
export function errorAttributes(error: unknown): Attributes {
  if (error instanceof Error) {
    return { error_name: error.name, error_message: error.message };
  }
  return { error_message: String(error) };
}

export const logger = {
  info: (message: string, attributes?: Attributes) =>
    emit("info", message, attributes),
  warn: (message: string, attributes?: Attributes) =>
    emit("warn", message, attributes),
  error: (message: string, attributes?: Attributes) =>
    emit("error", message, attributes),
  /** Log a caught error without risking leaking the raw object's contents. */
  exception: (message: string, error: unknown, attributes?: Attributes) =>
    emit("error", message, { ...attributes, ...errorAttributes(error) }),
};

export default logger;
